import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import AbcIcon from '@mui/icons-material/Abc';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, CircularProgress, FormControl, InputLabel, OutlinedInput, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { createIMaskInput } from '../WidgetFormText/Utils/createIMaskInput';
import { type } from './types';
const WidgetFormUsSsn = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const rawValue = props.getDataString();
    const valueIsMasked = rawValue.includes('*');
    const lastDigits = props.config.lastDigits;
    let value;
    if (valueIsMasked) {
        if (lastDigits === 4) {
            value = 'XXX-XX-****';
        }
        else if (lastDigits === 6) {
            value = 'XXX-**-****';
        }
        else {
            value = '***-**-****';
        }
    }
    else {
        value = (_a = props.internalData.value) !== null && _a !== void 0 ? _a : formatSsn(rawValue, props.config.lastDigits);
    }
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const error = getVisibleError(props);
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    const { t } = useCommonFormUiTranslation();
    const inputComponent = React.useMemo(() => (!valueIsMasked ? createIMaskInput(createSSNMask(valueIsMasked, !!props.config.maskInput, lastDigits)) : undefined), [valueIsMasked, props.config.maskInput, lastDigits]);
    const showEyeIcon = props.config.maskInput && !valueIsMasked && !props.config.disabled;
    const ariaDescribedBy = React.useId();
    const getWebkitTextSecurityProp = () => {
        return !props.internalData.showSsn ? 'disc' : 'none';
    };
    const handleShowSsn = () => {
        props.internalData.showSsn = !props.internalData.showSsn;
        props.update();
        renderForm(props);
    };
    return (React.createElement(FormControl, { sx: { width: (_c = props.config.style) === null || _c === void 0 ? void 0 : _c.width }, fullWidth: true, required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, error: !!error, disabled: valueIsMasked || props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, shrink: true, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(Box, { sx: { display: 'flex', gap: 2 } },
            React.createElement(OutlinedInput, { id: props.id, label: elementLabel, fullWidth: true, size: 'medium', required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, notched: true, error: !!error, value: value, disabled: props.config.disabled, readOnly: readOnly, onChange: (event) => {
                    const value = event.target.value;
                    if (value) {
                        const ssn = parseSsn(value, props.config.lastDigits);
                        if (ssn) {
                            props.setDataStringOrUndefined(ssn);
                            delete props.internalData.value;
                            delete props.internalData.error;
                        }
                        else {
                            props.setDataStringOrUndefined(undefined);
                            props.internalData.value = value;
                            props.internalData.error = props.config.label + ' ' + t('widget.widgetInput.isInvalid');
                        }
                    }
                    else {
                        props.setDataStringOrUndefined(undefined);
                        delete props.internalData.value;
                        delete props.internalData.error;
                    }
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, inputComponent: inputComponent, inputProps: Object.assign(Object.assign({ sx: Object.assign({}, (props.config.maskInput
                        ? { WebkitTextSecurity: !props.internalData.showSsn ? getWebkitTextSecurityProp() : undefined }
                        : {})) }, (props.editMode ? { tabIndex: -1 } : {})), { 'aria-describedby': ariaDescribedBy }), autoFocus: (_g = (!props.editMode && props.config.autoFocus)) !== null && _g !== void 0 ? _g : false, autoComplete: 'off', endAdornment: showEyeIcon &&
                    (props.internalData.loading ? (React.createElement(CircularProgress, { size: 24 })) : (React.createElement(InputAdornment, { position: 'end' },
                        React.createElement(Tooltip, { title: props.internalData.showSsn ? t('common.hide') : t('common.show') },
                            React.createElement(IconButton, { "aria-label": props.internalData.showSsn
                                    ? `${t('common.hide')} ${props.config.label}`
                                    : `${t('common.show')} ${props.config.label}`, onClick: handleShowSsn, edge: 'end' }, props.internalData.showSsn ? React.createElement(VisibilityOffOutlinedIcon, null) : React.createElement(VisibilityOutlinedIcon, null)))))) }),
            !props.config.disabled && (React.createElement(Button, { variant: 'outlined', startIcon: React.createElement(ReplayIcon, { fontSize: 'small' }), onClick: () => {
                    props.setDataStringOrUndefined(undefined);
                    delete props.internalData.value;
                    delete props.internalData.error;
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, "data-test": 'clear-button' }, t('common.clear')))),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
function createSSNMask(valueIsMasked, maskInput, lastDigits) {
    if (maskInput) {
        if (lastDigits === 4) {
            return {
                mask: 'XXX-XX-{end}',
                lazy: false, // for prefix to be visible when empty
                blocks: {
                    end: {
                        mask: '0000',
                        lazy: true, // makes [0000] optional and deletable
                    },
                },
            };
        }
        else if (lastDigits === 6) {
            return {
                mask: 'XXX-{end}',
                lazy: false, // for prefix to be visible when empty
                blocks: {
                    end: {
                        mask: '00-0000',
                        lazy: true, // makes [00]-[0000] optional and deletable
                    },
                },
            };
        }
        else {
            return {
                mask: '000-00-0000',
                lazy: true, // entire mask lazily revealed
            };
        }
    }
    else {
        let mask;
        if (valueIsMasked) {
            mask = undefined;
        }
        else if (lastDigits === 4) {
            mask = 'XXX-XX-0000';
        }
        else if (lastDigits === 6) {
            mask = 'XXX-00-0000';
        }
        else {
            mask = '000-00-0000';
        }
        return {
            mask: mask,
            overwrite: true,
            lazy: false,
            placeholderChar: '_',
        };
    }
}
function formatSsn(value, lastDigits) {
    if (!value) {
        return '';
    }
    if (lastDigits === 4) {
        const match = value.match(/(\d{4})$/);
        if (match) {
            return 'XXX-XX-' + match[1];
        }
    }
    else if (lastDigits === 6) {
        const match = value.match(/(\d{2})-?(\d{4})$/);
        if (match) {
            return 'XXX-' + match[1] + '-' + match[2];
        }
    }
    else {
        const match = value.match(/(\d{3})-?(\d{2})-?(\d{4})$/);
        if (match) {
            return match[1] + '-' + match[2] + '-' + match[3];
        }
    }
    return '';
}
function parseSsn(value, lastDigits) {
    if (lastDigits === 4) {
        if (/^X{3}-X{2}-\d{4}$/.test(value)) {
            return value.replace(/[X-]/g, '');
        }
    }
    else if (lastDigits === 6) {
        if (/^X{3}-\d{2}-\d{4}$/.test(value)) {
            return value.replace(/[X-]/g, '');
        }
    }
    else {
        if (/^\d{3}-\d{2}-\d{4}$/.test(value)) {
            return value.replace(/-/g, '');
        }
    }
    return undefined;
}
const WidgetFormUsSsnEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'SSN: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            readOnly: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'lastDigits',
                    label: t('widget.widgetInput.numberOfDigitsToInput'),
                    values: [
                        { label: t('widget.widgetLayout.allNine') },
                        { value: 4, label: t('widget.widgetInput.lastFour') },
                        { value: 6, label: t('widget.widgetInput.lastSix') },
                    ],
                },
                {
                    type: 'form.number',
                    key: 'rows',
                    label: t('common.rows'),
                    hidden: { when: '^multiline', dataNotEq: true },
                    style: { width: 200 },
                },
                {
                    type: 'form.checkbox',
                    key: 'emptyAsNull',
                    label: t('widget.widgetForm.emptyAsNull'),
                },
            ],
        }), buildValidateTab(props, { required: true }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormUsSsn, Object.assign({}, props))));
};
export const widgetFormUsSsn = {
    type,
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        if (props.internalData.error != null) {
            return props.internalData.error;
        }
        const value = props.getDataStringOrUndefined();
        if (value != null && !value.includes('*')) {
            if (props.config.lastDigits === 4) {
                if (!/^(?!0000)\d{4}$/.test(value)) {
                    return props.config.label + ' is invalid';
                }
            }
            else if (props.config.lastDigits === 6) {
                if (!/^(?!00)\d{2}(?!0000)\d{4}$/.test(value)) {
                    return props.config.label + ' is invalid';
                }
            }
            else {
                if (!/^(?!666|000|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}$/.test(value)) {
                    return props.config.label + ' is invalid';
                }
            }
        }
        else if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getDataStringOrUndefined();
        if (value) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), formatSsn(value, props.config.lastDigits));
        }
        else {
            return [];
        }
    },
    component: WidgetFormUsSsn,
    icon: React.createElement(AbcIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormUsSsnEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'ssn', label: 'SSN' }),
};
